import { IconServer } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type PushSubscriptionSummary,
  parseUserAgent,
  pushServiceHost,
} from '../lib/pushSubscriptions';
import { absolute } from '../lib/time';
import { EmptyState } from './EmptyState';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// Presenter for the push device list — pure over its props (no query, no fetch,
// no refetch). THIS browser's row is badged and has NO remove: the master
// `PushToggle` owns enable/disable here, so a bare server-delete would strand the
// local subscription and leave the toggle lying "on". Its container
// (`PushDevices.tsx`) owns the read/delete/refetch.

/** Compose a device label from the UA, falling back where parsing yields nothing. */
function deviceLabel(ua: string | null): string {
  const { browser, os } = parseUserAgent(ua);
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os ?? t('push_devices.unknown_device');
}

export function PushDeviceList(props: {
  subscriptions: readonly PushSubscriptionSummary[];
  currentEndpoint: string | null;
  onRemove: (endpoint: string) => void;
}): JSX.Element {
  return (
    <Show
      when={props.subscriptions.length > 0}
      fallback={<EmptyState message={t('push_devices.empty')} />}
    >
      <ul class="row-list push-devices__list">
        <For each={props.subscriptions}>
          {(sub) => {
            const isThis = (): boolean => sub.endpoint === props.currentEndpoint;
            return (
              <li class="row-list__row push-device">
                <div class="row-list__lead">
                  <span class="row-list__tile" aria-hidden="true">
                    <IconServer size="1rem" />
                  </span>
                  <span class="push-device__text">
                    <span class="push-device__name">{deviceLabel(sub.user_agent)}</span>
                    <span class="push-device__meta">
                      <code class="push-device__host">{pushServiceHost(sub.endpoint)}</code>
                      <span>
                        {t('push_devices.subscribed_on', { date: absolute(sub.created_at) })}
                      </span>
                      <Show when={isThis()}>
                        <StatusPill tone="success">{t('push_devices.this_device')}</StatusPill>
                      </Show>
                    </span>
                  </span>
                </div>
                <Show when={!isThis()}>
                  <div class="push-device__actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('push_devices.remove_label', {
                        device: deviceLabel(sub.user_agent),
                      })}
                      onClick={() => props.onRemove(sub.endpoint)}
                    >
                      {t('push_devices.remove')}
                    </Button>
                  </div>
                </Show>
              </li>
            );
          }}
        </For>
      </ul>
    </Show>
  );
}
